export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  add_paragraph: "Adding paragraph",
  add_comment: "Adding comment",
  reply_to_comment: "Replying to comment",
  redline_section: "Redlining section",
  find_throughout: "Finding every place",
  change_throughout: "Changing throughout",
  initialize_review: "Starting review",
  review_document: "Reviewing rule",
  request_focused_document: "Reading document",
  perplexity: "Searching the web",
  open_webpage: "Opening webpage",
  read_document: "Reading file",
  save_memory: "Saving memory",
  update_memory: "Updating memory",
  delete_memory: "Deleting memory",
  retrieve_memories_by_tag: "Looking up memories",
  search_memories: "Searching memories",
  send_guideline_suggestion: "Suggesting guideline",
  suggest_playbook_selection: "Suggesting playbook",
  create_new_playbook: "Creating playbook",
  create_playbook_workspace: "Creating playbook workspace",
  create_workspace_from_blueprint: "Creating workspace from blueprint",
  update_workspace_from_blueprint: "Updating workspace from blueprint",
  get_playbooks: "Loading playbooks",
  list_legacy_playbooks: "Loading legacy playbooks",
  read_playbook_rules: "Reading playbook",
  todo_list: "Updating to-do list",
  create_document: "Creating document",
  get_skill: "Loading skill",
  suggest_skill: "Suggesting skill",
  search_repository: "Searching repository",
  list_repository_fields: "Listing fields",
  get_repository_record: "Loading record",
  edit_non_focused_document: "Editing document",
  comment_non_focused_document: "Commenting on document",
  sub_agent: "Running sub-agent",
  execute_js: "Running script",
  grep: "Searching context",
  read_file: "Reading context",
  glob: "Browsing context",
  ls: "Browsing context",
  write_file: "Writing context",
  edit_file: "Editing context",
  execute: "Running command",
  add_attachment_to_inputs: "Staging file",
  save_table_artifact: "Saving table to Outputs",
  update_table_artifact: "Updating table",
  present_files: "Saving to Outputs",
  move_artifact: "Moving to Outputs",
  promote_artifact_to_workspace: "Sharing with workspace",
  delete_artifact: "Deleting from Outputs",
  read_table_artifact: "Reading saved table",
  live_review: "Running playbook review",
  send_feedback: "Reflecting",
  collect_input: "Requesting details",
  draft_email: "Drafting email",
  run_flow: "Running flow",
}

export const TOOL_DONE_NAMES: Record<string, string> = {
  add_paragraph: "Paragraph added",
  add_comment: "Comment added",
  reply_to_comment: "Reply added",
  redline_section: "Section redlined",
  find_throughout: "Places found",
  change_throughout: "Change proposed throughout",
  initialize_review: "Review started",
  review_document: "Rule reviewed",
  request_focused_document: "Document read",
  perplexity: "Web searched",
  open_webpage: "Webpage opened",
  read_document: "File read",
  save_memory: "Memory saved",
  update_memory: "Memory updated",
  delete_memory: "Memory deleted",
  retrieve_memories_by_tag: "Memories loaded",
  search_memories: "Memories searched",
  send_guideline_suggestion: "Guideline suggested",
  suggest_playbook_selection: "Playbook suggested",
  create_new_playbook: "Playbook created",
  create_playbook_workspace: "Playbook workspace created",
  create_workspace_from_blueprint: "Workspace created from blueprint",
  update_workspace_from_blueprint: "Workspace updated from blueprint",
  get_playbooks: "Playbooks loaded",
  list_legacy_playbooks: "Legacy playbooks loaded",
  read_playbook_rules: "Playbook read",
  todo_list: "To-do list updated",
  create_document: "Document saved to Outputs",
  get_skill: "Skill loaded",
  suggest_skill: "Skill suggested",
  search_repository: "Repository searched",
  list_repository_fields: "Fields listed",
  get_repository_record: "Record loaded",
  edit_non_focused_document: "Document edited",
  comment_non_focused_document: "Comment posted",
  sub_agent: "Sub-agent finished",
  execute_js: "Script run",
  grep: "Context searched",
  read_file: "Context read",
  glob: "Context browsed",
  ls: "Context browsed",
  write_file: "Context written",
  edit_file: "Context edited",
  execute: "Command run",
  add_attachment_to_inputs: "File staged",
  save_table_artifact: "Table saved to Outputs",
  update_table_artifact: "Table updated",
  present_files: "Saved to Outputs",
  move_artifact: "Moved to Outputs",
  promote_artifact_to_workspace: "Shared with workspace",
  delete_artifact: "Removed from Outputs",
  read_table_artifact: "Saved table read",
  live_review: "Playbook review complete",
  send_feedback: "Reflected",
  collect_input: "Details collected",
  draft_email: "Email drafted",
  run_flow: "Flow run finished",
}

function titleCaseToolName(toolName: string): string {
  return toolName.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
}

export function getToolDisplayName(toolName: string): string {
  return TOOL_DISPLAY_NAMES[toolName] ?? titleCaseToolName(toolName)
}

export function getToolDoneName(toolName: string): string {
  return TOOL_DONE_NAMES[toolName] ?? titleCaseToolName(toolName)
}

// Past-tense labels used when a step ended because the user pressed stop
// (status === "interrupted"). Most tools share the generic "<name> cancelled"
// form derived from the present-tense display name; entries here override
// that default when a more natural phrasing exists (e.g. "Sub-agent" vs.
// "Running sub-agent"). Keep this map sparse — falls back to the derived
// form for anything not listed.
const TOOL_INTERRUPTED_NAMES: Record<string, string> = {
  sub_agent: "Sub-agent cancelled",
  create_document: "Document creation cancelled",
  request_focused_document: "Document read cancelled",
  perplexity: "Web search cancelled",
  open_webpage: "Webpage open cancelled",
  download_webpage: "Webpage download cancelled",
  save_table_artifact: "Table save cancelled",
  update_table_artifact: "Table update cancelled",
  run_flow: "Flow run cancelled",
}

export function getToolInterruptedName(toolName: string): string {
  if (TOOL_INTERRUPTED_NAMES[toolName]) return TOOL_INTERRUPTED_NAMES[toolName]
  const display = TOOL_DISPLAY_NAMES[toolName] ?? titleCaseToolName(toolName)
  return `${display.replace(/\.{3}$/, "")} cancelled`
}

export function getToolFailedName(toolName: string, outcome: string): string {
  return `${getToolDisplayName(toolName)} ${outcome}`
}

// Tools whose tool_result content is markdown worth showing inline in the
// expanded body of a step row. Other tools (redline, comments, todo_list,
// suggest_*, etc.) have bespoke widgets and don't surface their raw result
// inside the ToolCallGroup.
export const COLLAPSIBLE_TOOLS = new Set(["perplexity", "open_webpage", "download_webpage", "sub_agent"])

// Sub-agent result tools whose tool_results, when produced inside a sub_agent,
// are lifted OUT of the sub-agent mini-chat and rendered as standalone
// top-level cards. The redline-family tools carry an OOXML payload; the
// suggest_* tools carry a JSON suggestion blob. In every case the tool_result
// preserves the real tool_name and the full `content` (sub_agent.py preserves
// both), so the existing MessageItem render path applies them with no extra
// code. Consumed by both groupAssistantSteps.ts (hoist out of the mini-chat)
// and ChatPage.tsx (re-order the card to the bottom of the turn).
export const HOISTED_SUBAGENT_RESULT_TOOLS = new Set([
  "redline_section",
  "redline_selection",
  "edit_table",
  "change_throughout",
  "add_paragraph",
  "add_comment",
  "reply_to_comment",
  "send_guideline_suggestion",
  "suggest_skill",
])

export const USER_INPUT_BLOCKING_TOOLS = ["collect_input", "render_agreement_review_form"] as const

export const COLLECT_INPUT_TOOLS = ["collect_input", "draft_email"] as const

export type CollectInputToolName = (typeof COLLECT_INPUT_TOOLS)[number]

export function isCollectInputTool(toolName: string | undefined): toolName is CollectInputToolName {
  return COLLECT_INPUT_TOOLS.includes(toolName as CollectInputToolName)
}

export function isEmailHandOffTool(toolName: string | undefined): boolean {
  return toolName === "draft_email"
}

export const DOCUMENT_CHANGE_TOOL_NAMES = new Set([
  "redline_section",
  "redline_selection",
  "edit_table",
  "change_throughout",
  "add_paragraph",
  "add_comment",
  "reply_to_comment",
  "live_review",
  "review_document",
  "review_in_parallel",
  "execute_js",
])
